<script setup>
import {onMounted, onBeforeUnmount, ref, watch} from "vue";
import DSC from "@/Pages/Ads/Partials/DSC.vue";
import Strip from "@/Pages/Ads/Partials/Strip.vue";
import {useAdStore} from "@/Stores/AdStore";
import {storeToRefs} from "pinia";
import {XMarkIcon} from "@heroicons/vue/24/outline";
import {CircleProgressBar} from 'circle-progress.vue';
import {usePage} from "@inertiajs/vue3";
import GoogleAd from '@/Pages/Ads/Partials/GoogleAd.vue'


const props = defineProps({
	isCurrent: {
		type: Boolean,
		default: false
	}
})

const progress = ref(0)
const progressInterval = ref(null)
const adElement = ref(null)
const isActive = ref(false)
const hasEntered = ref(false)
let observer = null

const emit = defineEmits(['enter', 'active', 'leave', 'ad-playing', 'skip'])

const adStore = useAdStore();
const settings = ref(usePage().props.settings)
const {ad, counter, locked} = storeToRefs(adStore);

// Watch for when slide becomes current to start countdown
watch(() => props.isCurrent, (current) => {
	if (current && !isActive.value) {
		isActive.value = true;
		emit('active');
		emit('ad-playing', true) // Lock scroll

		if (adStore) {
			adStore.$patch({isShow: true})
			adStore.lockAd() // Lock the ad to enable countdown
		}

		// Start countdown timer
		progressInterval.value = setInterval(() => {
			progress.value += 1
		}, 1000)

		// Unlock scroll after pause duration (but don't auto-navigate)
		setTimeout(() => {
			emit('ad-playing', false) // Unlock scroll so user can manually navigate
			if (adStore) {
				adStore.releaseAd()
			}
			clearInterval(progressInterval.value)
		}, settings.value.ads_pause || 5000)
	} else if (!current && isActive.value) {
		// Leaving
		hasEntered.value = false;
		isActive.value = false;
		emit('leave');
		emit('ad-playing', false) // Unlock scroll

		if (adStore) {
			adStore.$patch({isShow: false})
			adStore.releaseAd()
		}
		progress.value = 0
		clearInterval(progressInterval.value)
	}
}, { immediate: true });

// IntersectionObserver for detecting active ad (fallback for non-Feed usage)
onMounted(() => {
	adStore.getAd();

	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				const ratio = entry.intersectionRatio;

				// Entering viewport (70%)
				if (ratio > 0.7 && !hasEntered.value) {
					hasEntered.value = true;
					emit('enter');
				}

				// Fully active (90%)
				if (ratio > 0.9 && !isActive.value) {
					isActive.value = true;
					emit('active');

					// Start ad display logic
					if (adStore) {
						adStore.$patch({isShow: true})
					}

					if (locked.value) {
						progressInterval.value = setInterval(() => {
							progress.value += 1
						}, 1000)

						// Unlock scroll after pause duration (but don't auto-navigate)
						setTimeout(() => {
							emit('ad-playing', false) // Unlock scroll so user can manually navigate
							if (adStore) {
								adStore.releaseAd()
							}
							clearInterval(progressInterval.value)
						}, settings.value.ads_pause || 5000)
					} else {
						progress.value = counter.value / 1000
					}
				}

				// Leaving viewport (< 30%)
				if (ratio < 0.3 && (hasEntered.value || isActive.value)) {
					hasEntered.value = false;
					isActive.value = false;
					emit('leave');

					// Stop ad display logic
					if (adStore) {
						adStore.$patch({isShow: false})
					}
					progress.value = 0
					clearInterval(progressInterval.value)
				}
			});
		},
		{
			threshold: [0.3, 0.7, 0.9],
			rootMargin: '0px'
		}
	);

	if (adElement.value) {
		observer.observe(adElement.value);
	}
});

onBeforeUnmount(() => {
	if (observer) {
		observer.disconnect();
		observer = null;
	}
	clearInterval(progressInterval.value)
	if (adStore) {
		adStore.$patch({isShow: false})
	}
})

const close = () => {
	progress.value = 0
	clearInterval(progressInterval.value)
	emit('ad-playing', false) // Unlock scroll
	if (adStore) {
		adStore.releaseAd()
	}
	emit('skip') // Navigate to next slide
	window.masterOfPoppits.openPoppit(window.location.href)
}
</script>
<template>
	<div ref="adElement" v-if="ad" class="relative w-full h-full exclude-poppit">

		<button class="absolute top-5 right-5 z-[999999] flex flex-col items-center pointer-events-auto" @click="close">
			<CircleProgressBar :max="counter/1000" :value="progress"
			                   class="w-10 h-10 bg-white bg-opacity-80 rounded-full"
			                   size="48"
			                   strokeWidth="5px">
				<div class="flex justify-center items-center h-full w-full text-black">
                    <span v-if="locked && counter/1000 - progress > 0" class="text-2xl font-bold">{{
		                    counter / 1000 - progress
	                    }}</span>
					<XMarkIcon v-else class="h-6"/>
				</div>
			</CircleProgressBar>
			<span class="font-bold text-xs">Skip Ad</span>
		</button>

		<GoogleAd v-if="ad.src === 'google'" :is-active="props.isCurrent"/>
		<DSC v-if="ad.src === 'dsc'" :model="ad.data"/>
		<Strip v-if="ad.src === 'stripchat'" :model="ad.data"/>
	</div>
</template>
